import { useCallback } from 'react'
import { Moon, Briefcase, Waves, Bot } from 'lucide-react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { ActivityEnum } from '../../types'
import { formatCurrency, formatDuration, formatUnderscoreCase } from '../../utils'
import { JOBS } from '../../constants/jobs'
import { BASE_ENERGY_REGENERATION_PER_HOUR, MIN_ENERGY_FOR_ACTIVITY_START } from '../../constants/game'
import { Link } from 'react-router-dom'
import LOCATIONS from '../../constants/locations'
import { Tooltip, Checkbox } from '@nextui-org/react'

export default function ActivitySelector() {
  const { gameData, setCurrentActivity, setGameData } = useGameplayContext()
  const { currentActivity, currentJob, currencies, surfSession, automations, equipment } = gameData

  const activities: {
    name: ActivityEnum
    icon: any
    label: string
    color: string
    activeColor: string
    borderBColor: string
    disabledColor: string
  }[] = [
    {
      name: 'rest',
      icon: Moon,
      label: 'Rest',
      color: 'bg-purple-500',
      activeColor: 'bg-purple-600',
      borderBColor: 'border-b-purple-600',
      disabledColor: 'bg-purple-300'
    },
    {
      name: 'work',
      icon: Briefcase,
      label: 'Work',
      color: 'bg-yellow-500',
      activeColor: 'bg-yellow-600',
      borderBColor: 'border-b-yellow-600',
      disabledColor: 'bg-yellow-300'
    },
    {
      name: 'surf',
      icon: Waves,
      label: 'Surf',
      color: 'bg-blue-500',
      activeColor: 'bg-blue-600',
      borderBColor: 'border-b-blue-600',
      disabledColor: 'bg-blue-300'
    }
  ]

  const handleActivityChange = useCallback(
    (activity: ActivityEnum) => {
      if (activity === 'rest') {
        setCurrentActivity(activity)
        return
      }

      if (activity === 'surf') {
        const surfCost = LOCATIONS[gameData.currentLocation].surfSessionCost
        if (currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START && currencies.money.amount >= surfCost) {
          setCurrentActivity(activity)
        }
        return
      }

      if (currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START) {
        setCurrentActivity(activity)
      }
    },
    [currencies.energy.amount, currencies.money.amount, gameData.currentLocation, setCurrentActivity]
  )

  const handleAutomationToggle = useCallback(
    (activity: ActivityEnum) => {
      setGameData((prev) => ({
        ...prev,
        automations: {
          ...prev.automations,
          [activity]: {
            ...prev.automations[activity],
            isActive: !prev.automations[activity].isActive
          }
        }
      }))
    },
    [setGameData]
  )

  const getActivityStatus = useCallback(() => {
    switch (currentActivity) {
      case 'rest':
        return `Resting ${
          currencies.energy.amount === currencies.energy.max
            ? '(Energy Full)'
            : `(+${formatCurrency(
                BASE_ENERGY_REGENERATION_PER_HOUR * (currencies.energy.totalMultiplier || 1)
              )} Energy/hour)`
        }`
      case 'work':
        const job = currentJob ? JOBS[currentJob] : null
        return <Link to="/jobs">{job ? `Working as ${job.name} (${formatCurrency(job.income)}/hour)` : 'No job'}</Link>
      case 'surf':
        const waveCount = surfSession.appliedSessionData.length
        return (
          <Link to="/surf">{`Surfing ${formatDuration(surfSession.elapsedTime)} / ${formatDuration(
            surfSession.totalTime
          )} - ${waveCount} wave${waveCount !== 1 ? 's' : ''}`}</Link>
        )
      default:
        return ''
    }
  }, [currentActivity, currentJob, surfSession])

  const isActivityDisabled = (activity: ActivityEnum) => {
    if (activity === 'rest') return false
    if (activity === 'surf') {
      const surfCost = LOCATIONS[gameData.currentLocation].surfSessionCost
      return (
        currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START ||
        currencies.money.amount < surfCost ||
        !equipment.surfboard
      )
    }
    return currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START
  }

  const getActiveActivity = () => activities.find((a) => a.name === currentActivity) || activities[0]

  const getRequirementsMessage = (activity: ActivityEnum) => {
    if (activity === 'rest') return ''

    const needsEnergy = currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START
    const needsMoney =
      activity === 'surf' && currencies.money.amount < LOCATIONS[gameData.currentLocation].surfSessionCost

    if (needsEnergy && needsMoney) {
      return `Need ${formatCurrency(
        MIN_ENERGY_FOR_ACTIVITY_START - currencies.energy.amount
      )} more energy and $${formatCurrency(
        LOCATIONS[gameData.currentLocation].surfSessionCost - currencies.money.amount
      )} more money`
    } else if (needsEnergy) {
      return `Need ${formatCurrency(MIN_ENERGY_FOR_ACTIVITY_START - currencies.energy.amount)} more energy`
    } else if (needsMoney) {
      return `Need $${formatCurrency(
        LOCATIONS[gameData.currentLocation].surfSessionCost - currencies.money.amount
      )} more money`
    } else if (!equipment.surfboard) {
      return `Need to equip a surfboard`
    }
    return ''
  }

  const renderActivityButton = (activity: (typeof activities)[0], index: number) => {
    const isDisabled = isActivityDisabled(activity.name)
    const isActive = currencies.energy.amount <= 0 ? activity.name === 'rest' : currentActivity === activity.name
    const automationState = automations[activity.name]

    const button = (
      <div className="flex-1 flex flex-col relative" key={activity.name}>
        {automationState.isUnlocked && (
          <div className="absolute top-0 right-1 z-10 flex items-center">
            <div
              className={`flex items-center px-1 py-1 rounded ${
                isActive ? activity.activeColor : isDisabled ? activity.disabledColor : activity.color
              }`}
            >
              <Tooltip
                content={
                  automationState.isActive
                    ? `Disable ${formatUnderscoreCase(activity.name)} Automation`
                    : `Enable ${formatUnderscoreCase(activity.name)} Automation`
                }
                className="bg-white backdrop-blur-sm text-sm"
              >
                <Bot className="w-4 h-4 text-white relative" />
              </Tooltip>
              <Checkbox
                size="md"
                isSelected={automationState.isActive}
                onValueChange={() => activity.name !== 'rest' && handleAutomationToggle(activity.name)}
                isDisabled={activity.name === 'rest'}
                color={activity.name === 'rest' ? 'secondary' : activity.name === 'surf' ? 'primary' : 'warning'}
                classNames={{
                  base: 'm-0 p-0 w-4 h-4'
                }}
              />
            </div>
          </div>
        )}
        <button
          key={activity.name}
          onClick={() => handleActivityChange(activity.name)}
          className={`flex-1 flex items-center justify-center py-6 px-2 transition-all ${
            isActive ? activity.activeColor : isDisabled ? activity.disabledColor : activity.color
          } ${index === 0 ? 'rounded-l-lg' : ''} ${index === activities.length - 1 ? 'rounded-r-lg' : ''} ${
            isDisabled ? 'cursor-not-allowed' : ''
          }`}
          disabled={isDisabled}
        >
          <activity.icon className={`w-6 h-6 mr-2  ${isDisabled ? 'text-gray-500' : 'text-white'}`} />
          <span className={`text-lg font-medium ${isDisabled ? 'text-gray-500' : 'text-white'}`}>{activity.label}</span>
        </button>
      </div>
    )

    if (isDisabled && activity.name !== 'rest') {
      return (
        <Tooltip
          key={activity.name}
          content={getRequirementsMessage(activity.name)}
          className="bg-white bg-opacity-80 backdrop-blur-sm text-sm"
        >
          {button}
        </Tooltip>
      )
    }

    return button
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 h-full flex flex-col sm:col-span-2">
      <div className="flex rounded-lg overflow-hidden">{activities.map(renderActivityButton)}</div>
      <div className="relative mt-2">
        <div
          className={`w-0 h-0 ${
            getActiveActivity().borderBColor
          } border-l-[10px] border-l-transparent border-r-[10px] border-r-transparent border-b-[10px] absolute ${
            currentActivity === 'rest' ? 'left-[16.67%]' : currentActivity === 'work' ? 'left-1/2' : 'left-[83.33%]'
          } -top-[10px] transform -translate-x-1/2`}
        ></div>
        <div className={`w-full py-2 px-4 rounded-lg ${getActiveActivity().activeColor} text-center`}>
          <span className="font-semibold text-sm text-white">{getActivityStatus()}</span>
        </div>
      </div>
    </div>
  )
}
